import React from "react";
import {
  XCircle,
  TrendingDown,
  ShoppingCart,
  Globe2,
  Eye,
  PhoneCall,
} from "lucide-react";

const BULLETS = [
  { icon: XCircle, text: "Forget traditional ordering methods" },
  { icon: TrendingDown, text: "Find the most competitive prices" },
  { icon: ShoppingCart, text: "Easy procurement via online tenders" },
  { icon: Globe2, text: "Watch manufacturer & supplier network" },
  { icon: Eye, text: "Transparent, fully auditable process" },
  { icon: PhoneCall, text: "Contact directly with manufacturers" },
];

const STATS = [
  { value: "$2B", label: "Transaction Volume" },
  { value: "1,000+", label: "Completed Orders" },
  { value: "30+", label: "Markets Served" },
];

export default function ValueProposition() {
  return (
    <section
      data-testid="value-prop-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-4xl mx-auto text-center reveal mb-14">
          <h2
            data-testid="value-prop-title"
            className="font-display font-black text-3xl md:text-5xl leading-[1.1] tracking-tight"
          >
            <span className="text-slate-900">
              Change Your Traditional Inefficient Ordering Method with
            </span>{" "}
            <span className="text-slate-900">Commodity</span>
            <span className="text-red-600">Bid</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative reveal">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-600/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-red-500/10 rounded-full blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-100">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=900&fit=crop"
                alt="Two business professionals collaborating"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating price card */}
            <div className="hidden md:flex absolute -bottom-6 left-8 items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-xl border border-slate-100">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingDown size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Avg. savings
                </p>
                <p className="text-lg font-extrabold text-slate-900">
                  -27% cost
                </p>
              </div>
            </div>
          </div>

          {/* Bullets */}
          <div className="space-y-3 md:space-y-4 reveal">
            {BULLETS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.text}
                  data-testid={`value-bullet-${i}`}
                  className="group flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-white border border-slate-200/70 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md shadow-blue-600/20 group-hover:scale-110 transition-transform">
                    <Icon size={18} className="text-white" strokeWidth={2.5} />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm md:text-base">
                    {b.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-${i}`}
              className="reveal text-center p-8 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/70 hover:border-blue-200 hover:shadow-lg transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <p className="font-display font-black text-4xl md:text-6xl leading-none bg-gradient-to-br from-blue-600 to-blue-700 bg-clip-text text-transparent">
                {s.value}
              </p>
              <p className="mt-3 text-sm md:text-base font-semibold text-slate-600 uppercase tracking-wider">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
